'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { computeCountdown } from '@federa/utils/dates/time-sync';
import { TIME_UNITS } from '@federa/const/time-units';
import { POLLING_INTERVALS } from '@federa/const/timing';
import { useVisibilityChangeListener } from '@federa/components/hooks/usePageVisibility';
import { useLatestRef } from './useLatestRef.ts';

/** Auction countdown with tab-visibility sync: decrement every second, re-sync on visibility change (background throttling) and every 5s while visible. */
export function useCountdown(auctionEndUtc?: string | null): number | null {
	const [remaining, setRemaining] = useState<number | null>(() => computeCountdown(auctionEndUtc));
	const [prevAuctionEnd, setPrevAuctionEnd] = useState(auctionEndUtc);
	const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
	// `useLatestRef` owns the post-commit effect write: under concurrent React a render-time ref write can be observed by siblings or interleaved renders.
	const auctionEndRef = useLatestRef(auctionEndUtc);

	// Recompute on prop change WITHOUT an effect: store the previous prop in state and re-derive during render, so React never commits the intermediate frame.
	if (auctionEndUtc !== prevAuctionEnd) {
		setPrevAuctionEnd(auctionEndUtc);
		setRemaining(computeCountdown(auctionEndUtc));
	}

	// Countdown timer decrementing every second; no dependencies -- the functional updater handles null/zero cases.
	useEffect(() => {
		const timer = setInterval(() => {
			setRemaining((prev) => {
				if (prev == null || prev <= 0) return prev;
				return prev - 1;
			});
		}, TIME_UNITS.SECOND_MS);
		return () => clearInterval(timer);
	}, []);

	// `auctionEndRef` is listed because `useLatestRef` is opaque to the exhaustive-deps checker. A ref identity is stable
	// for the component's life, so this callback's own identity stays stable too and the effects keyed on it below still
	// run once rather than per render.
	const startSyncInterval = useCallback(() => {
		syncIntervalRef.current ??= setInterval(() => {
			setRemaining(computeCountdown(auctionEndRef.current));
		}, POLLING_INTERVALS.countdownSync);
	}, [auctionEndRef]);

	const stopSyncInterval = useCallback(() => {
		if (syncIntervalRef.current) {
			clearInterval(syncIntervalRef.current);
			syncIntervalRef.current = null;
		}
	}, []);

	// Sync countdown on tab visibility change, fixing background throttling and catching auction extensions.
	useVisibilityChangeListener(
		useCallback(() => {
			if (document.visibilityState === 'visible') {
				setRemaining(computeCountdown(auctionEndRef.current));
				startSyncInterval();
			} else {
				stopSyncInterval();
			}
		}, [startSyncInterval, stopSyncInterval, auctionEndRef]),
	);

	// Start periodic sync interval if initially visible; clean up on unmount
	useEffect(() => {
		if (document.visibilityState === 'visible') {
			startSyncInterval();
		}
		return stopSyncInterval;
	}, [startSyncInterval, stopSyncInterval]);

	return remaining;
}
